interface UseMarkdownEditorArgs {
  textAreaRef: HTMLTextAreaElement | undefined;
  text: string;
  setText: (val: string) => void;
}

export const useVisualEditor = ({ textAreaRef, text, setText }: UseMarkdownEditorArgs) => {
  const surroundSelection = (
    characters: string,
    options?: { endingCharacters?: string; selectedStart?: number; selectedEnd?: number }
  ) => {
    if (!textAreaRef) return;

    const { selectionStart, selectionEnd } = textAreaRef;

    // Replaces the selected portion of the textarea or just inserts link at cursor position 0:
    const updatedText = `${text.substring(0, selectionStart)}${characters}${text.substring(selectionStart, selectionEnd)}${options?.endingCharacters ?? characters}${text.substring(selectionEnd)}`;

    try {
      // Focuses on the text area then selects all the text and uses `insertText` to replace it programmatically.
      textAreaRef.focus();
      textAreaRef.setSelectionRange(0, text.length);
      // `execCommand` is deprecated but there isn't an equivalent alternative to it. Using `execCommand`
      // allows the user to maintain their native undo/redo history:
      document.execCommand('insertText', false, updatedText);
    } catch {
      // if `execCommand` fails, fall back to changing state via React, rather than a user event. We lose undo
      // doing it this way:
      setText(updatedText);
    }

    // Add slight pause to allow text to update before inserting cursor:
    setTimeout(() => {
      if (!textAreaRef) return;

      textAreaRef.focus();

      const cursorPosition = selectionEnd + (options?.endingCharacters ?? characters).length;
      textAreaRef.setSelectionRange(options?.selectedStart ?? cursorPosition, options?.selectedEnd ?? cursorPosition);
    }, 100);
  };

  const makeSelectionBold = () => {
    surroundSelection('**');
  };

  const makeSelectionItalic = () => {
    surroundSelection('*');
  };

  const makeSelectionCode = () => {
    surroundSelection('`');
  };

  const makeSelectionLink = () => {
    if (!textAreaRef) return;

    const { selectionStart, selectionEnd } = textAreaRef;

    const selectedText = text.slice(selectionStart, selectionEnd);

    if (selectedText.match(/^http(s)?:\/\//)) {
      surroundSelection('[](', {
        endingCharacters: ')',
        selectedStart: selectionStart + 1,
        selectedEnd: selectionStart + 1,
      });
      return;
    }

    surroundSelection('[', {
      endingCharacters: '](url)',
      selectedStart: selectionStart + selectedText.length + 3,
      selectedEnd: selectionStart + selectedText.length + 6,
    });
  };

  return {
    makeSelectionBold,
    makeSelectionItalic,
    makeSelectionLink,
    makeSelectionCode,
  };
};
